import { useEffect, useState } from 'react';
import { getModuleEntitlements } from '@/api/modules';
import type { ModuleEntitlements } from '@/api/modules';

/** The export changes rarely, and the server enforces it on every call: this only shapes the screen. */
const TTL_MS = 60_000;

let cached: { at: number; value: Promise<ModuleEntitlements> } | null = null;

function load(): Promise<ModuleEntitlements> {
  if (!cached || Date.now() - cached.at > TTL_MS) {
    const value = getModuleEntitlements();
    cached = { at: Date.now(), value };
    value.catch(() => {
      cached = null;
    });
  }
  return cached.value;
}

export function resetModulesForTest(): void {
  cached = null;
}

/** A module's state: `true`/`false` once known, `null` while loading or when the read failed. */
export function useModuleEnabled(module: string): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    load()
      .then((view) => {
        if (live) setEnabled(view.modules.find((m) => m.module === module)?.enabled ?? false);
      })
      .catch(() => {
        if (live) setEnabled(null);
      });
    return () => {
      live = false;
    };
  }, [module]);
  return enabled;
}
